import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Button,
  EmptyState,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tabs,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';

import { useShipmentsQuery } from '../api';
import { AttentionBadge, ShipmentStatusBadge } from '../components/ShipmentBadges';
import { ShipmentDetailDrawer } from '../components/ShipmentDetailDrawer';
import { SHIPMENT_TABS, type ShipmentTab } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useJibexAccountQuery } from '@/features/delivery/jibex-account/api';
import { formatCatalogPrice } from '@/features/products/format-price';
import { useActiveShop } from '@/features/shops/use-active-shop';

const TAB_KEY: Record<ShipmentTab, MessageKey> = {
  in_progress: 'merchant.ship.tab.in_progress',
  to_send: 'merchant.ship.tab.to_send',
  failed: 'merchant.ship.tab.failed',
  delivered: 'merchant.ship.tab.delivered',
  returns: 'merchant.ship.tab.returns',
  attention: 'merchant.ship.tab.attention',
};

/**
 * « Expéditions » — every parcel sent to Jibex: the tab it is in, its status (from Jibex's notifications and polls),
 * and what the merchant can do. Pickups (« enlèvements ») are booked at Jibex directly, not from Souk.
 */
export function ShipmentsPage(): ReactElement {
  const { t, locale } = useI18n();
  const [tab, setTab] = useState<ShipmentTab>('in_progress');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<string | null>(null);
  const shipments = useShipmentsQuery(tab, page);
  const shop = useActiveShop();
  const isOwner = shop.activeMembership?.roleKey === 'merchant_owner';
  // Owner-only on the API: a staff member simply does not get this banner.
  const account = useJibexAccountQuery(isOwner);

  const counts = shipments.data?.counts;
  const items = shipments.data?.items ?? [];
  const noAccount = isOwner && account.isSuccess && account.data.account === null;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="sk-page-title">{t('merchant.ship.title')}</h1>
          <p className="text-sm text-inkFaint">{t('merchant.ship.subtitle')}</p>
        </div>
        <Link to="/delivery/account" className="text-sm font-medium text-accent hover:underline">
          {t('merchant.delivery.account.linkFromDelivery')}
        </Link>
      </div>

      {noAccount ? (
        <p
          role="status"
          className="rounded-md border border-warn bg-warnSoft px-4 py-3 text-sm text-ink"
        >
          {t('merchant.ship.noAccount')}{' '}
          <Link to="/delivery/account" className="font-medium text-accent hover:underline">
            {t('merchant.ship.noAccountLink')}
          </Link>
        </p>
      ) : null}

      <Tabs
        label={t('merchant.ship.title')}
        tabs={SHIPMENT_TABS.map((id) => ({
          id,
          label:
            counts === undefined ? t(TAB_KEY[id]) : `${t(TAB_KEY[id])} (${String(counts[id])})`,
        }))}
        activeId={tab}
        onChange={(id) => {
          setTab(id as ShipmentTab);
          setPage(1);
        }}
      >
        {shipments.isPending ? (
          <TableSkeleton columns={7} />
        ) : shipments.isError ? (
          <EmptyState
            title={
              shipments.error.status === 403
                ? t('merchant.common.accessDenied')
                : t('merchant.delivery.loadError')
            }
            description={t(resolveErrorMessageKey(shipments.error))}
          />
        ) : items.length === 0 ? (
          <EmptyState
            title={t('merchant.ship.empty.title')}
            description={t('merchant.ship.empty.description')}
          />
        ) : (
          <>
            <Table caption={t('merchant.ship.tableCaption')}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t('merchant.ship.col.order')}</TableHeaderCell>
                  <TableHeaderCell>{t('merchant.ship.col.recipient')}</TableHeaderCell>
                  <TableHeaderCell>{t('merchant.ship.col.status')}</TableHeaderCell>
                  <TableHeaderCell>{t('merchant.ship.col.tracking')}</TableHeaderCell>
                  <TableHeaderCell>{t('merchant.ship.col.updated')}</TableHeaderCell>
                  <TableHeaderCell>
                    <span className="sr-only">{t('merchant.ship.col.open')}</span>
                  </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <span className="font-mono text-sm text-ink">#{item.orderNumber}</span>
                      <span className="block text-xs text-inkFaint">
                        {formatCatalogPrice(Number(item.orderTotal))}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-ink" dir="auto">
                        {item.recipientName}
                      </span>
                      <span className="block text-xs text-inkFaint" dir="auto">
                        {item.cityOverride ?? `${item.governorate}, ${item.delegation}`}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <ShipmentStatusBadge status={item.status} />
                        {item.attentionReason !== null ? (
                          <AttentionBadge reason={item.attentionReason} />
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-ink">
                        {item.trackingNumber ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-inkFaint">
                        {new Date(item.updatedAt).toLocaleString(
                          locale === 'ar' ? 'ar-TN' : 'fr-TN',
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setOpenId(item.id);
                        }}
                      >
                        {t('merchant.ship.action.open')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {shipments.data.total > shipments.data.pageSize ? (
              <Pagination
                page={page}
                pageCount={Math.ceil(shipments.data.total / shipments.data.pageSize)}
                onPageChange={setPage}
              />
            ) : null}
          </>
        )}
      </Tabs>

      <ShipmentDetailDrawer
        shipmentId={openId}
        onClose={() => {
          setOpenId(null);
        }}
      />
    </div>
  );
}
